import { useEffect } from "react";
import { useLocation } from "react-router-dom";

export type Locale = "en" | "zh";

export const messages = {
  en: {
    nav: {
      inventory: "Inventory",
      trade: "Trade/Sell",
      story: "Our Story",
      contact: "Contact",
      call: "Call us",
      menu: "Open menu",
      closeMenu: "Close menu",
      main: "Main navigation",
      skip: "Skip to content",
      language: "中文",
      footerNote: "A considered way to find your next car in Flushing.",
      visitCall: "Visit / call",
      explore: "Explore",
      privacy: "Privacy",
      terms: "Terms",
      roadAhead: "Built for the road ahead.",
    },
    common: {
      loading: "Loading",
      error: "Something went wrong. Please try again.",
      vehicle: "Vehicle",
      preowned: "Pre-owned",
      priceRequest: "Call for price",
      mileageRequest: "Mileage on request",
      available: "Available",
      pending: "Pending",
      sold: "Sold",
      hidden: "Hidden",
      draft: "Draft",
      close: "Close",
    },
    home: {
      location: "Flushing, New York · Locally owned",
      title: "Find Your Next Car",
      subtitle: "The Biggest Wholesale Dealer in NY",
      browse: "Browse inventory",
      trade: "Trade or sell your car",
      proofInventory: "Curated local inventory",
      proofVisit: "Visit us in Flushing",
      handpicked: "01 / Handpicked",
      featured: "Featured vehicles",
      viewAll: "View all inventory",
      arriving: "New vehicles are arriving soon.",
      seeInventory: "See inventory",
      findFit: "02 / Find your fit",
      browseBy: "Browse by",
      make: "make.",
      makeIntro:
        "Start with a name you trust. Our live inventory changes as vehicles find new homes.",
      exploreAll: "Explore all",
      vehicle: "vehicle",
      vehicles: "vehicles",
      ycWay: "03 / The YC way",
      betterWay: "A better way to",
      shopLocal: "shop local.",
      whyFallback:
        "Straightforward inventory, clear details, and a local team ready to help you choose with confidence.",
      clearTitle: "Clear details",
      clearCopy: "Useful specs and honest photos for every vehicle.",
      humanTitle: "Human help",
      humanCopy: "Call or send a note—no maze of forms.",
      localTitle: "Local roots",
      localCopy: "Find us on Northern Boulevard in Flushing.",
      ready: "Ready when you are",
      findRight: "Let’s find the",
      rightOne: "right one.",
      talk: "Talk to YC Auto",
      unavailable: "Live inventory is temporarily unavailable.",
    },
    inventory: {
      metaTitle: "Inventory | YC Auto USA",
      metaDescription:
        "Browse current pre-owned vehicles at YC Auto USA in Flushing, New York.",
      eyebrow: "Inventory / Live selection",
      title: "Find your next",
      titleAccent: "everyday.",
      vehiclesExplore: "vehicles\nto explore",
      filter: "Filter inventory",
      filters: "Filters",
      closeFilters: "Close filters",
      show: "Show vehicles",
      sort: "Sort",
      newest: "Newest added",
      priceLow: "Price: low to high",
      priceHigh: "Price: high to low",
      mileageLow: "Mileage: low to high",
      yearNew: "Year: newest",
      loading: "Loading inventory",
      none: "No vehicles match those filters.",
      noneCopy:
        "Try widening your search or clear the filters to see the full selection.",
      clear: "Clear filters",
      make: "Make",
      allMakes: "All makes",
      minYear: "Min year",
      maxYear: "Max year",
      anyYear: "Any year",
      minPrice: "Min price",
      maxPrice: "Max price",
      maxMileage: "Max mileage",
      anyMileage: "Any mileage",
      under: "Under",
      body: "Body type",
      anyBody: "Any body type",
      sedan: "Sedan",
      truck: "Truck",
      wagon: "Wagon",
      coupe: "Coupe",
      drivetrain: "Drivetrain",
      anyDrive: "Any drivetrain",
      clearAll: "Clear all filters",
      previous: "Previous page",
      next: "Next page",
      pages: "Inventory pages",
      loadError: "Unable to load inventory.",
    },
    detail: {
      loading: "Loading vehicle",
      notFound: "Vehicle not found.",
      inventory: "Inventory",
      preownedVehicle: "Pre-owned vehicle",
      statusSold:
        "This vehicle is sold. Explore similar available vehicles below.",
      statusPending:
        "This vehicle is pending. Ask us about timing and alternatives.",
      call: "Call about this car",
      text: "Text us",
      availability: "Check availability",
      testDrive: "Schedule a test drive",
      stock: "Stock no.",
      highlights: "Highlights",
      more: "More to consider",
      similar: "Similar vehicles",
      seeAll: "See all",
      question: "Have a question?",
      makeIt: "Make it",
      yours: "yours.",
      questionCopy:
        "Tell us what you’re looking for and we’ll get right back to you.",
      browseAvailable: "Browse available inventory",
      send: "Send a message",
      ask: "Ask about this vehicle",
      callShort: "Call",
      messageShort: "Message",
      imageView: "view",
      specs: {
        Year: "Year",
        Make: "Make",
        Model: "Model",
        Trim: "Trim",
        "Body type": "Body type",
        Drivetrain: "Drivetrain",
        Transmission: "Transmission",
        "Fuel type": "Fuel type",
        Engine: "Engine",
        Exterior: "Exterior",
        Interior: "Interior",
      },
    },
    about: {
      metaTitle: "Our Story | YC Auto USA",
      metaDescription:
        "Meet YC Auto USA, a local pre-owned vehicle dealer in Flushing, New York.",
      eyebrow: "Our Story / YC Auto USA",
      title: "Local knowledge.",
      accent: "Good cars.",
      lede: "A straightforward place to find your next vehicle in Flushing, New York.",
      rail: "ABOUT\nTHE SHOP",
      photoAlt: "The Your Choice Auto Group team at the Flushing showroom",
      caption: "Your Choice Auto Group · Flushing, New York",
      pace: "Browse at your pace",
      paceCopy: "Start online, then visit when it feels right.",
      person: "Ask a real person",
      personCopy: "Call or send a note—whatever is easiest.",
      local: "Find us locally",
      inventory: "See current inventory",
    },
    staff: {
      heading: "Meet Our Staff",
      team: "Our people",
      intro:
        "Get to know the people behind YC Auto. Reach out by phone, email, or WeChat.",
      phone: "Phone",
      email: "Email",
      wechat: "WeChat",
      unavailable: "Not provided",
      missingHint: "Gray buttons: contact details not yet provided.",
      wechatId: "WeChat ID",
      wechatHelp:
        "Copy this ID, then search for it in WeChat to add this team member.",
      copy: "Copy WeChat ID",
      copied: "WeChat ID copied.",
      copyManually: "Please select and copy the WeChat ID above.",
    },
    contact: {
      metaTitle: "Contact | YC Auto USA",
      metaDescription:
        "Meet the YC Auto USA team, learn our story, and find contact details and directions in Flushing, New York.",
      heading: "Contact",
      story: "Our Story",
      company: "Company information",
      staff: "Meet Our Staff",
      directions: "Visit us",
      sections: "On this page",
      eyebrow: "Contact / We’re here",
      title: "Let’s talk",
      accent: "cars.",
      intro: "Our story, our people, and a direct way to reach us.",
      call: "Call",
      visit: "Visit",
      note: "Send a note",
      getBack: "We’ll get",
      getBackAccent: "back to you.",
      timing: "Usually within one business day.",
      hours: "Mon–Fri 10:00 AM–6:00 PM · Sat–Sun 12:00 PM–5:00 PM",
    },
    trade: {
      metaTitle: "Trade or Sell Your Car | YC Auto USA",
      metaDescription:
        "Tell YC Auto USA about your current vehicle and request a trade or purchase conversation.",
      eyebrow: "Trade / Sell",
      title: "Your car may be",
      accent: "our next one.",
      intro:
        "Share a few essentials. We’ll review your vehicle and contact you about the next step.",
      step1: "Vehicle details",
      step1Copy: "Have your VIN and current mileage ready.",
      step2: "A real review",
      step2Copy: "Our team will look over the information you send.",
      step3: "No obligation",
      step3Copy: "A request starts a conversation—not a commitment.",
      formEyebrow: "Tell us about your car",
      formTitle: "Start your request.",
    },
    map: {
      eyebrow: "Find us",
      title: "Visit us in Flushing.",
      directions: "Get directions",
      frameTitle: "Map showing YC Auto USA in Flushing, New York",
    },
    lead: {
      asking: "Asking about",
      name: "Name *",
      namePlaceholder: "Your name",
      phone: "Phone",
      email: "Email",
      preferred: "Preferred contact",
      phoneCall: "Phone call",
      message: "Message",
      vehicleMessage: "Is this vehicle still available?",
      helpMessage: "How can we help?",
      verification: "Security verification",
      verifyError: "Please complete the verification and try again.",
      tryAgain: "Please try again.",
      received: "Message received.",
      thanks:
        "Thanks for reaching out. A member of the YC Auto team will follow up shortly.",
      tradeReceived: "Trade/Sell request received.",
      tradeThanks:
        "Thank you. Our team will review your vehicle and contact you using the details provided.",
      another: "Send another message",
      anotherTrade: "Submit another vehicle",
      privacyBefore: "By submitting, you agree to our",
      privacy: "privacy policy",
      sending: "Sending…",
      requestDrive: "Request a test drive",
      send: "Send message",
      submitTrade: "Send vehicle details",
      vin: "VIN / Chassis number *",
      vinPlaceholder: "17-character VIN",
      mileage: "Mileage *",
      mileagePlaceholder: "Current mileage",
      wechat: "WeChat",
      wechatPlaceholder: "WeChat ID",
      contactHint: "Enter at least one: phone, email, or WeChat.",
      invalidVin: "Enter a valid 17-character VIN without I, O, or Q.",
      invalidMileage: "Enter the current mileage.",
      invalidContact: "Enter a phone number, email, or WeChat ID.",
    },
    legal: {
      privacy: "Privacy policy",
      terms: "Terms of use",
      updated: "Last updated September 24, 2026",
      infoTitle: "Information we receive",
      info: "When you contact YC Auto USA, we receive the details you choose to share, including your name, contact details and message. Trade/Sell requests may also include a VIN, vehicle mileage and WeChat ID. A pre-approval request collects your name, Social Security number, residential address, time at that address, phone number, email, and any financing selections you provide.",
      useTitle: "How we use it",
      use: "We use submitted information to respond to your request, evaluate a vehicle you may trade or sell, and keep customer records. Pre-approval identity and contact details are encrypted separately; authorized dealership administrators must explicitly open them, and access is recorded. Notification emails contain a receipt and protected admin link, not your application details. Submitting this form does not itself run a credit check, transmit an application to a lender, or approve financing. We do not sell customer information or send it to vehicle-data vendors. Contact us to request a correction or deletion; do not include your Social Security number in email or ordinary messages.",
      analyticsTitle: "Cookies and analytics",
      analytics:
        "The site may use Cloudflare Web Analytics for aggregate traffic measurement. The website does not require an account or use advertising trackers to browse inventory.",
      contactTitle: "Contact",
      contact: "Questions about this policy can be sent to",
      inventoryTitle: "Inventory information",
      inventory:
        "Vehicle availability, pricing, mileage, and descriptions can change without notice. A vehicle shown as available is not reserved until confirmed by YC Auto USA.",
      websiteTitle: "Website use",
      website:
        "Please use the website and contact forms for lawful purposes. We may decline messages that are abusive, automated, or unrelated to vehicle inquiries.",
      accuracyTitle: "Accuracy",
      accuracy:
        "We work to keep information current, but the website may contain occasional errors. Contact us to confirm details before making a purchasing decision.",
      questionsTitle: "Questions",
      questions: "For questions about these terms, call",
    },
    notFound: {
      eyebrow: "404 / Not found",
      title: "We couldn’t find that page.",
      copy: "Try browsing the current inventory or return home.",
      browse: "Browse inventory",
    },
  },
  zh: {
    nav: {
      inventory: "在售车辆",
      trade: "置换/卖车",
      story: "关于我们",
      contact: "联系我们",
      call: "致电咨询",
      menu: "打开菜单",
      closeMenu: "关闭菜单",
      main: "主导航",
      skip: "跳到主要内容",
      language: "English",
      footerNote: "在法拉盛，用更轻松的方式找到下一辆车。",
      visitCall: "到店 / 电话",
      explore: "浏览",
      privacy: "隐私政策",
      terms: "使用条款",
      roadAhead: "为下一段旅程而准备。",
    },
    common: {
      loading: "加载中",
      error: "出现问题，请稍后再试。",
      vehicle: "车辆",
      preowned: "二手车",
      priceRequest: "价格请咨询",
      mileageRequest: "里程请咨询",
      available: "在售",
      pending: "预订中",
      sold: "已售",
      hidden: "已隐藏",
      draft: "草稿",
      close: "关闭",
    },
    home: {
      location: "纽约法拉盛 · 本地车行",
      title: "找到你的下一辆车",
      subtitle: "纽约最大的汽车批发商",
      browse: "浏览在售车辆",
      trade: "置换或出售车辆",
      proofInventory: "精选本地库存",
      proofVisit: "欢迎到访法拉盛门店",
      handpicked: "01 / 精选车辆",
      featured: "推荐车辆",
      viewAll: "查看全部库存",
      arriving: "新车源即将上线。",
      seeInventory: "查看库存",
      findFit: "02 / 找到合适选择",
      browseBy: "按品牌",
      make: "选车。",
      makeIntro: "从熟悉的品牌开始。车辆售出或上新后，列表会实时变化。",
      exploreAll: "浏览全部",
      vehicle: "辆车",
      vehicles: "辆车",
      ycWay: "03 / YC 的方式",
      betterWay: "更轻松地",
      shopLocal: "在本地选车。",
      whyFallback:
        "实时库存、清晰车辆信息，以及随时为你提供帮助的本地团队，让选车更简单、更安心。",
      clearTitle: "信息清晰",
      clearCopy: "每辆车都提供实用参数和真实照片。",
      humanTitle: "真人服务",
      humanCopy: "电话或留言即可，不需要填写复杂流程。",
      localTitle: "扎根本地",
      localCopy: "门店位于法拉盛 Northern Boulevard。",
      ready: "准备好时，我们就在这里",
      findRight: "一起找到",
      rightOne: "适合你的车。",
      talk: "联系 YC Auto",
      unavailable: "当前无法加载实时库存。",
    },
    inventory: {
      metaTitle: "在售车辆｜YC Auto USA",
      metaDescription: "浏览 YC Auto USA 位于纽约法拉盛的当前二手车库存。",
      eyebrow: "在售车辆 / 实时库存",
      title: "找到你的下一辆",
      titleAccent: "日常座驾。",
      vehiclesExplore: "辆车\n可供浏览",
      filter: "筛选库存",
      filters: "筛选",
      closeFilters: "关闭筛选",
      show: "显示车辆",
      sort: "排序",
      newest: "最新上架",
      priceLow: "价格：从低到高",
      priceHigh: "价格：从高到低",
      mileageLow: "里程：从低到高",
      yearNew: "年份：从新到旧",
      loading: "正在加载库存",
      none: "没有符合条件的车辆。",
      noneCopy: "请调整筛选条件，或清除筛选查看全部车辆。",
      clear: "清除筛选",
      make: "品牌",
      allMakes: "全部品牌",
      minYear: "最低年份",
      maxYear: "最高年份",
      anyYear: "不限年份",
      minPrice: "最低价格",
      maxPrice: "最高价格",
      maxMileage: "最高里程",
      anyMileage: "不限里程",
      under: "低于",
      body: "车身类型",
      anyBody: "不限车身类型",
      sedan: "轿车",
      truck: "皮卡",
      wagon: "旅行车",
      coupe: "双门轿跑",
      drivetrain: "驱动方式",
      anyDrive: "不限驱动方式",
      clearAll: "清除全部筛选",
      previous: "上一页",
      next: "下一页",
      pages: "库存分页",
      loadError: "无法加载库存。",
    },
    detail: {
      loading: "正在加载车辆",
      notFound: "未找到这辆车。",
      inventory: "在售车辆",
      preownedVehicle: "二手车",
      statusSold: "这辆车已售出。你可以在下方浏览类似的在售车辆。",
      statusPending: "这辆车正在预订中，欢迎咨询进度或其他选择。",
      call: "电话咨询这辆车",
      text: "短信联系",
      availability: "询问是否在售",
      testDrive: "预约试驾",
      stock: "库存编号",
      highlights: "配置亮点",
      more: "更多选择",
      similar: "相似车辆",
      seeAll: "查看全部",
      question: "还有问题？",
      makeIt: "让它成为",
      yours: "你的下一辆车。",
      questionCopy: "告诉我们你的需求，我们会尽快联系你。",
      browseAvailable: "浏览在售车辆",
      send: "发送消息",
      ask: "咨询这辆车",
      callShort: "电话",
      messageShort: "留言",
      imageView: "图片",
      specs: {
        Year: "年份",
        Make: "品牌",
        Model: "型号",
        Trim: "配置",
        "Body type": "车身类型",
        Drivetrain: "驱动方式",
        Transmission: "变速箱",
        "Fuel type": "燃料类型",
        Engine: "发动机",
        Exterior: "外观颜色",
        Interior: "内饰颜色",
      },
    },
    about: {
      metaTitle: "关于我们｜YC Auto USA",
      metaDescription: "了解位于纽约法拉盛的本地二手车行 YC Auto USA。",
      eyebrow: "关于我们 / YC Auto USA",
      title: "熟悉本地。",
      accent: "认真选车。",
      lede: "在纽约法拉盛，用简单透明的方式找到下一辆车。",
      rail: "关于\n车行",
      photoAlt: "Your Choice Auto Group 团队在法拉盛展厅的合影",
      caption: "Your Choice Auto Group · 纽约法拉盛",
      pace: "按自己的节奏浏览",
      paceCopy: "先在线了解，觉得合适再到店。",
      person: "直接联系真人",
      personCopy: "电话或留言，选择最方便的方式。",
      local: "就在法拉盛",
      inventory: "查看当前库存",
    },
    staff: {
      heading: "认识我们的团队",
      team: "我们的团队",
      intro: "认识优选汽车的工作人员，通过电话、邮箱或微信直接联系。",
      phone: "电话",
      email: "邮箱",
      wechat: "微信",
      unavailable: "暂未提供",
      missingHint: "灰色按钮：该联系方式暂未提供。",
      wechatId: "微信号",
      wechatHelp: "复制微信号后，在微信中搜索并添加这位工作人员。",
      copy: "复制微信号",
      copied: "微信号已复制。",
      copyManually: "请选中上方微信号并手动复制。",
    },
    contact: {
      metaTitle: "联系我们｜YC Auto USA",
      metaDescription:
        "了解优选汽车的故事与团队，查看纽约法拉盛门店信息、联系方式和路线。",
      heading: "联系我们",
      story: "我们的故事",
      company: "公司信息",
      staff: "认识我们的团队",
      directions: "到访门店",
      sections: "本页导航",
      eyebrow: "联系我们 / 随时欢迎",
      title: "聊聊你的",
      accent: "选车需求。",
      intro: "了解我们的故事与团队，找到适合你的联系方式。",
      call: "电话",
      visit: "到店",
      note: "发送留言",
      getBack: "我们会尽快",
      getBackAccent: "联系你。",
      timing: "通常会在一个工作日内回复。",
      hours: "周一至周五 10:00–18:00 · 周六、周日 12:00–17:00",
    },
    trade: {
      metaTitle: "置换或卖车｜YC Auto USA",
      metaDescription: "向 YC Auto USA 提交车辆资料，咨询车辆置换或出售。",
      eyebrow: "置换 / 卖车",
      title: "你的这辆车，也许是",
      accent: "我们的下一辆车。",
      intro: "提交几项基本资料，我们会评估车辆并联系你说明下一步。",
      step1: "准备车辆资料",
      step1Copy: "请准备好 VIN 车架号和当前里程。",
      step2: "真人评估",
      step2Copy: "我们的工作人员会查看你提交的信息。",
      step3: "没有强制承诺",
      step3Copy: "提交资料只是开始沟通，不代表必须交易。",
      formEyebrow: "介绍一下你的车",
      formTitle: "开始提交。",
    },
    map: {
      eyebrow: "门店位置",
      title: "欢迎到访法拉盛门店。",
      directions: "获取路线",
      frameTitle: "YC Auto USA 纽约法拉盛门店地图",
    },
    lead: {
      asking: "正在咨询",
      name: "姓名 *",
      namePlaceholder: "请输入姓名",
      phone: "电话",
      email: "邮箱",
      preferred: "首选联系方式",
      phoneCall: "电话",
      message: "留言",
      vehicleMessage: "请问这辆车还在售吗？",
      helpMessage: "请告诉我们如何帮助你。",
      verification: "安全验证",
      verifyError: "请完成安全验证后再提交。",
      tryAgain: "请稍后再试。",
      received: "留言已收到。",
      thanks: "感谢联系。YC Auto 工作人员会尽快回复你。",
      tradeReceived: "车辆资料已收到。",
      tradeThanks: "感谢提交。我们会评估车辆，并通过你提供的联系方式联系你。",
      another: "再发送一条留言",
      anotherTrade: "提交另一辆车",
      privacyBefore: "提交即表示你同意我们的",
      privacy: "隐私政策",
      sending: "正在发送…",
      requestDrive: "提交试驾预约",
      send: "发送留言",
      submitTrade: "提交车辆资料",
      vin: "VIN / 车架号 *",
      vinPlaceholder: "17 位 VIN 车架号",
      mileage: "里程 *",
      mileagePlaceholder: "当前里程",
      wechat: "微信",
      wechatPlaceholder: "微信号",
      contactHint: "电话、邮箱或微信至少填写一项。",
      invalidVin: "请输入 17 位有效 VIN，不能包含 I、O 或 Q。",
      invalidMileage: "请输入车辆当前里程。",
      invalidContact: "请至少填写电话、邮箱或微信中的一项。",
    },
    legal: {
      privacy: "隐私政策",
      terms: "使用条款",
      updated: "更新日期：2026 年 9 月 24 日",
      infoTitle: "我们收集的信息",
      info: "当你联系 YC Auto USA 时，我们会收到你主动提交的姓名、联系方式和留言。置换/卖车申请还可能包含 VIN 车架号、车辆里程和微信号。预批申请会收集你的姓名、社安号、居住地址、在该地址的居住时间、电话、邮箱，以及你选择的贷款估算条件。",
      useTitle: "信息用途",
      use: "我们使用这些信息回复咨询、评估你希望置换或出售的车辆，并保存客户记录。预批申请的身份及联系方式单独加密保存；获授权的车行管理员需主动打开查看，查看操作会被记录。通知邮件只包含收件提示和受保护的后台链接，不包含申请资料。提交此表单本身不会查询信用、向贷款机构递交申请或批准贷款。我们不会出售客户资料，也不会把资料发送给车辆数据供应商。如需更正或删除资料，请联系我们；请勿在邮件或普通留言中提供社安号。",
      analyticsTitle: "Cookie 与统计",
      analytics:
        "网站可能使用 Cloudflare Web Analytics 进行汇总流量统计。浏览库存无需注册账号，网站也不使用广告追踪器。",
      contactTitle: "联系我们",
      contact: "如对本政策有疑问，请发送邮件至",
      inventoryTitle: "库存信息",
      inventory:
        "车辆库存状态、价格、里程和描述可能随时变化。网站显示在售并不代表车辆已为你保留，请联系 YC Auto USA 确认。",
      websiteTitle: "网站使用",
      website:
        "请合法使用网站和联系表单。对于辱骂、自动发送或与车辆无关的信息，我们可能不予回复。",
      accuracyTitle: "信息准确性",
      accuracy:
        "我们会尽力保持信息准确，但网站仍可能出现个别错误。做出购买决定前，请联系我们确认车辆详情。",
      questionsTitle: "问题咨询",
      questions: "如对使用条款有疑问，请致电",
    },
    notFound: {
      eyebrow: "404 / 页面不存在",
      title: "没有找到这个页面。",
      copy: "你可以浏览当前库存，或返回首页。",
      browse: "浏览在售车辆",
    },
  },
} as const;

export function localeFromPath(pathname: string): Locale {
  return pathname === "/zh" || pathname.startsWith("/zh/") ? "zh" : "en";
}

export function stripLocale(pathname: string): string {
  if (pathname === "/zh") return "/";
  return pathname.startsWith("/zh/") ? pathname.slice(3) || "/" : pathname;
}

export function localizedPath(path: string, locale: Locale): string {
  if (/^(?:https?:|mailto:|tel:|sms:|#)/i.test(path)) return path;
  const [pathAndQuery, hash = ""] = path.split("#", 2);
  const [pathname, query = ""] = pathAndQuery.split("?", 2);
  const bare = stripLocale(pathname || "/");
  const localized =
    locale === "zh" ? (bare === "/" ? "/zh" : `/zh${bare}`) : bare;
  return `${localized}${query ? `?${query}` : ""}${hash ? `#${hash}` : ""}`;
}

export function useLocale() {
  const location = useLocation();
  const locale = localeFromPath(location.pathname);
  useEffect(() => {
    document.documentElement.lang = locale === "zh" ? "zh-CN" : "en";
  }, [locale]);
  return {
    locale,
    isZh: locale === "zh",
    copy: messages[locale],
    path: (value: string) => localizedPath(value, locale),
    switchPath: localizedPath(
      `${location.pathname}${location.search}${location.hash}`,
      locale === "zh" ? "en" : "zh",
    ),
  };
}

export function formatLocalizedPrice(
  value: number | null,
  locale: Locale,
): string {
  if (value === null || !Number.isFinite(value))
    return messages[locale].common.priceRequest;
  return new Intl.NumberFormat(locale === "zh" ? "zh-CN" : "en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  }).format(value / 100);
}

export function formatLocalizedMileage(
  value: number | null,
  locale: Locale,
): string {
  if (value === null || !Number.isFinite(value))
    return messages[locale].common.mileageRequest;
  const number = new Intl.NumberFormat(
    locale === "zh" ? "zh-CN" : "en-US",
  ).format(value);
  return locale === "zh" ? `${number} 英里` : `${number} mi`;
}
